import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { SessionService } from '@src/app/shared/services/session.service';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingInterceptor implements HttpInterceptor {

  private redirectingToLogin = false;
  private redirectTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private injector: Injector,
    public sessionService: SessionService,
    private router: Router
  ) { }

  private get session() {
    return this.injector.get(SessionService);
  }

  private get toasterService() {
    return this.injector.get(ToasterService);
  }

  private get translateService() {
    return this.injector.get(TranslateService);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleError(error, request);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return throwError(() => error);
      }),
      finalize(() => {
      })
    );
  }

  private isLoginRoute(path: string): boolean {
    return path === '/' || path.startsWith('/login');
  }

  private isLoginRequest(request: HttpRequest<any>): boolean {
    return request.url.includes('/login');
  }

  private handleError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<HttpEvent<any>> {
    console.log(error);

    const currentPath = this.router.url.split('?')[0];
    const onLoginPage = this.isLoginRoute(currentPath);

    const apiCode = error?.error?.code;
    const isLoginReq = this.isLoginRequest(request);
    if (apiCode === '40102' && !isLoginReq) {
      if (!onLoginPage && !this.redirectingToLogin) {
        this.redirectingToLogin = true;
        const toastDuration = 6000;
        // display toast before redirect after token exp
        this.toasterService.show(
          'warning',
          this.translateService.instant('ERRORS.TOKEN_EXPIRED_TITLE'),
          this.translateService.instant('ERRORS.TOKEN_EXPIRED_BODY'),
          toastDuration
        );

        const previousURL = this.router.url;
        const loginRedirectDuration = 4000;
        // redirect after a short delay (so user can see the toast)
        this.redirectTimer = setTimeout(() => {
          this.sessionService.logout();
          this.router.navigate(['/login'], {
            queryParams: { previousURL },
            replaceUrl: true
          }).finally(() => {
            this.redirectingToLogin = false;
          });
        }, loginRedirectDuration);
      }

      this.sessionService.setLoading(false);
      return throwError(() => error);
    }

    // login page: view hide auth/session toasts that aren't login errors
    if (onLoginPage && !isLoginReq && (apiCode === '40101' || apiCode === '40301')) {
      this.sessionService.setLoading(false);
      return throwError(() => error);
    }

    const requestPath = request.url.split('?')[0];
    const isWarrantyGlobalSearchRequest = requestPath.endsWith('/api/Warranties/search/code') || requestPath.endsWith('/api/Warranties/search/serial-number');

    // hide toasts for specific routes
    if (
      !isWarrantyGlobalSearchRequest &&
      !error?.url?.includes('/challenge') &&
      !error?.url?.includes('/logout') &&
      !this.router.url.includes('/certificate')
    ) {
      if (error.error && error.error.code) {
        const code = error.error.code;
        const title = `${code} - ${error.error.error || 'Error'}`;
        const body = this.getErrorMessage(code, error);

        this.toasterService.show('error', title, body, 6000);
      } else {
        const defaultTitle = `Error ${error.status}`;
        const defaultBody = error.message || 'An unknown error occurred.';
        this.toasterService.show('error', defaultTitle, defaultBody, 6000);
      }
    }
    this.sessionService.setLoading(false);
    return throwError(() => error);
  }

  private getErrorMessage(code: any, error: any) {
    const translate = this.translateService;
    switch (code) {
      case '40001': return translate.instant('ERRORS.40001');
      case '40101': return translate.instant('ERRORS.40101');
      case '40301': return translate.instant('ERRORS.40301');
      case '40401': return translate.instant('ERRORS.40401');
      case '40402': return translate.instant('ERRORS.40402');
      case '40901': return translate.instant('ERRORS.40901');
      case '40902': return translate.instant('ERRORS.40902');
      case '50001': return translate.instant('ERRORS.50001');
      default:
        if (error.error.details && Array.isArray(error.error.details)) {
          return error.error.details.map((detail: any) =>
            translate.instant('ERRORS.FIELD_ISSUE', {
              field: detail.field,
              issue: detail.issue
            })
          ).join(', ');
        }
        return error.error.message || translate.instant('ERRORS.GENERIC_ERROR');
    }
  }
}
